import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { WorkpayApiError } from '@/lib/apiEnvelope';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button, TextInput } from '@components/ui';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import Modal from '@components/ui/Modal/Modal';
import { createJobTitle, updateJobTitle } from '@/services/jobTitleService';
import { useDepartmentsForCascade } from '@features/departments/hooks/useDepartmentsForCascade';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import type {
  CreateJobTitlePayload,
  JobTitleDto,
} from '@/types/jobTitle';

import styles from '@features/departments/DepartmentModals.module.scss';

interface CreateEditJobTitleModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: JobTitleDto | null;
}

interface JobTitleFormValues {
  name: string;
  code: string;
  departmentIds: string[];
}

const DEFAULT_VALUES: JobTitleFormValues = { name: '', code: '', departmentIds: [] };

function CreateEditJobTitleModal({
  isOpen,
  onClose,
  editData,
}: CreateEditJobTitleModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const isEditMode = Boolean(editData);

  const { departments, isLoading: depsLoading } = useDepartmentsForCascade();
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ label: d.name, value: d.id })),
    [departments],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    setError,
    formState: { errors },
  } = useForm<JobTitleFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      reset({
        name: editData.name,
        code: editData.code,
        departmentIds: editData.departments.map((d) => d.id),
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, editData, reset]);

  const handleMutationError = useCallback(
    (error: unknown) => {
      const e = error as WorkpayApiError;
      // 41046 = WP_JOB_TITLE_CODE_ALREADY_EXISTS, 41045 = WP_JOB_TITLE_NAME_ALREADY_EXISTS.
      if (e.code === 41046) {
        setError('code', { type: 'server', message: t('K_VALIDATION_CODE_DUPLICATE', 'This code is already in use') });
        return;
      }
      if (e.code === 41045) {
        setError('name', { type: 'server', message: t('K_VALIDATION_NAME_DUPLICATE', 'This name is already in use') });
        return;
      }
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
    [addNotification, setError, t],
  );

  const invalidateCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['job-titles'] });
    // All cascade variants ([key, departmentId]) need refetch — predicate match.
    void queryClient.invalidateQueries({ queryKey: ['job-titles-cascade'] });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateJobTitlePayload) => createJobTitle(payload),
    onSuccess: () => {
      invalidateCaches();
      reset();
      onClose();
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: CreateJobTitlePayload) =>
      updateJobTitle(editData!.id, payload),
    onSuccess: () => {
      invalidateCaches();
      reset();
      onClose();
    },
    onError: handleMutationError,
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const onSubmit = useCallback(
    (values: JobTitleFormValues) => {
      const payload: CreateJobTitlePayload = {
        name: values.name.trim(),
        code: values.code.trim(),
        departments: values.departmentIds.map((id) => ({ id })),
      };
      if (isEditMode) {
        updateMutation.mutate(payload);
      } else {
        createMutation.mutate(payload);
      }
    },
    [isEditMode, createMutation, updateMutation],
  );

  const handleClose = useCallback(() => {
    if (!mutation.isPending) {
      reset();
      onClose();
    }
  }, [mutation.isPending, reset, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditMode
          ? t('K_MODAL_EDIT_JOB_TITLE', 'Edit Job Title')
          : t('K_MODAL_CREATE_JOB_TITLE', 'Create Job Title')
      }
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            type="submit"
            form="job-title-form"
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? t('K_BUTTON_SAVING', 'Saving...')
              : isEditMode
                ? t('K_UPDATE', 'Update')
                : t('K_SAVE', 'Save')}
          </Button>
        </div>
      }
    >
      <form id="job-title-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextInput
          label={t('K_LABEL_NAME', 'Name')}
          isRequired
          fullWidth
          placeholder={t('K_PLACEHOLDER_JOB_TITLE_NAME', 'e.g. Senior Engineer')}
          error={errors.name?.message}
          {...register('name', {
            required: t('K_VALIDATION_NAME_REQUIRED', 'Name is required'),
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />
        <TextInput
          label={t('K_LABEL_CODE', 'Code')}
          isRequired
          fullWidth
          placeholder={t('K_PLACEHOLDER_JOB_TITLE_CODE', 'e.g. SR_ENG')}
          error={errors.code?.message}
          {...register('code', {
            required: t('K_VALIDATION_CODE_REQUIRED', 'Code is required'),
            maxLength: { value: 100, message: t('K_VALIDATION_MAX_100', 'Max 100 characters') },
          })}
        />
        <Controller
          name="departmentIds"
          control={control}
          rules={{
            validate: (v) =>
              v.length > 0 ||
              t('K_VALIDATION_JOB_TITLE_DEPARTMENTS_REQUIRED', 'At least one department is required'),
          }}
          render={({ field }) => (
            <MultiSelect
              label={t('K_LABEL_DEPARTMENTS', 'Departments')}
              isRequired
              placeholder={t('K_PLACEHOLDER_SELECT_DEPARTMENT', 'Select a department')}
              options={departmentOptions}
              value={field.value}
              onChange={field.onChange}
              loading={depsLoading}
              error={errors.departmentIds?.message}
            />
          )}
        />
      </form>
    </Modal>
  );
}

export default CreateEditJobTitleModal;
